import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import useJobsPagination from "./shared/useJobsPagination";
import AmazonSourceSelector, {
  AMAZON_MODES,
  buildAmazonSourceFormData,
} from "./shared/AmazonSourceSelector";

const STATUS_DONE = "done";
const STATUS_FAILED = "failed";

const sourceLabel = (j) =>
  j.input_mode === "vendor"
    ? `Vendor: ${j.vendor}`
    : j.input_mode === "sku"
    ? `Parent SKU: ${j.parent_sku}`
    : `File: ${j.input_filename || "—"}`;

function AmazonListingsExportTool() {
  const [mode, setMode] = useState(AMAZON_MODES.VENDOR);
  const [file, setFile] = useState(null);
  const [vendor, setVendor] = useState("");
  const [parentSku, setParentSku] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: "/amazon/listings-export/jobs",
    cacheKey: "amazonListingsExport.jobs",
    pageSize: 10,
  });

  const toolInfo = {
    toolName: "Amazon Listings & Price Export",
    description:
      "Side-by-side Shopify vs Amazon snapshot (price, compare-at, quantity, ASIN, status) for a vendor, an uploaded parent-SKU list, or a single parent SKU. Read-only.",
    steps: [
      "Choose the products: a Shopify vendor, an uploaded parent-SKU file, or a single parent SKU.",
      "Click Run. Products + prices come from Shopify and are matched to Amazon by SKU.",
      "Leave the page if you like — the job runs server-side and reappears here.",
      "Download the XLSX — 'Shopify vs Amazon' has one row per variant with both sides.",
    ],
    templates: [],
  };

  const guideTips = [
    "Read-only — never changes anything on Amazon.",
    "Shopify is the source of truth; the Amazon side is matched by exact variant SKU.",
    "'Not on Amazon' means we found the Shopify variant but no matching Amazon listing.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prevStatus = previous[j.job_id];
      if (prevStatus && prevStatus !== j.status) {
        if (j.status === STATUS_DONE) addToast(`Export ${j.job_id} complete.`, "success");
        else if (j.status === STATUS_FAILED)
          addToast(`Export ${j.job_id} failed: ${j.error || "unknown error"}`, "error");
      }
    });
    previousStatusRef.current = Object.fromEntries(jobs.map((j) => [j.job_id, j.status]));
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    let formData;
    try {
      formData = buildAmazonSourceFormData({ mode, file, vendor, parentSku });
    } catch (e) {
      addToast(e.message, "error");
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await flaskApi.post("/amazon/listings-export", formData, {
        onUploadProgress: (e) =>
          setUploadProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      addToast(`Export ${jobId} queued. Tracking progress…`, "info");
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setFile(null);
    } catch (error) {
      console.error(error);
      const message = await extractErrorMessage(error, "Failed to start export.");
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `/amazon/listings-export/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const cd =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      link.setAttribute(
        "download",
        extractFilenameFromHeader(cd) || job.result_filename || "amazon_listings_export.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("File downloaded.", "success");
    } catch (error) {
      console.error(error);
      const message = await extractErrorMessage(error, "Failed to download file.");
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Amazon Listings & Price Export"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <AmazonSourceSelector
              mode={mode}
              setMode={setMode}
              file={file}
              setFile={setFile}
              vendor={vendor}
              setVendor={setVendor}
              parentSku={parentSku}
              setParentSku={setParentSku}
              disabled={uploading}
            />

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={uploading}>
                {uploading ? "Starting…" : "Run Export"}
              </button>
              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Export Jobs"
          emptyText="No export jobs yet. Choose a source and click Run Export."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
            const progress = j.progress || {};
            const total = Number(progress.total) || 0;
            const processed = Number(progress.processed) || 0;
            const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
            return (
              <div key={j.job_id} className="tool-job-status">
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Job ID</span>
                  <span className="tool-job-value">{j.job_id}</span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Source</span>
                  <span className="tool-job-value">{sourceLabel(j)}</span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Status</span>
                  <span className={`tool-job-value tool-job-status-${j.status}`}>{j.status}</span>
                </div>
                {total > 0 ? (
                  <>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Progress</span>
                      <span className="tool-job-value">
                        {processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)
                      </span>
                    </div>
                    <div className="progress-bar-container">
                      <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                  </>
                ) : null}
                {progress.matched !== undefined ? (
                  <div className="tool-job-counts">
                    <span>Matched: {progress.matched || 0}</span>
                    <span>Not on Amazon: {progress.not_on_amazon || 0}</span>
                  </div>
                ) : null}
                {j.status === STATUS_FAILED && j.error ? (
                  <div className="tool-job-error">Error: {j.error}</div>
                ) : null}
                {j.status === STATUS_DONE ? (
                  <button
                    type="button"
                    className="tool-btn-primary"
                    onClick={() => handleDownload(j)}
                    disabled={downloadingJobId === j.job_id}
                  >
                    {downloadingJobId === j.job_id ? "Downloading…" : "Download XLSX"}
                  </button>
                ) : null}
              </div>
            );
          }}
        />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}

export default AmazonListingsExportTool;
